import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { useRecentSearches } from '../RecentSearches';

export const isoDay = (d) => d.toISOString().slice(0, 10);
export const fmtDur = (s) => {
    s = Math.round(s || 0);
    if (s < 60) return `${s}s`;
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    return h ? `${h}h ${m}m` : `${m}m`;
};
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-');

export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-');

/** Sortable, searchable table. `cols`: [{ key, label, num, render, value }]. */
export const DataTable = ({ rows, cols, searchKeys, csvName, empty, onRowClick }) => {
    const [sort, setSort] = useState({ key: cols[1]?.key, dir: -1 });
    const [q, setQ] = useState('');
    const recentSearch = useRecentSearches('usage-report', { value: q, onPick: setQ });

    const shown = useMemo(() => {
        const needle = q.trim().toLowerCase();
        let out = needle
            ? rows.filter(r => searchKeys.some(k => String(k(r) || '').toLowerCase().includes(needle)))
            : rows;
        const col = cols.find(c => c.key === sort.key);
        if (col) {
            const val = col.value || ((r) => r[col.key]);
            out = [...out].sort((a, b) => {
                const x = val(a), y = val(b);
                if (x === y) return 0;
                if (x == null) return 1;
                if (y == null) return -1;
                return (x > y ? 1 : -1) * sort.dir;
            });
        }
        return out;
    }, [rows, q, sort, cols, searchKeys]);

    const exportCsv = () => {
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const lines = [cols.map(c => esc(c.label)).join(',')];
        shown.forEach(r => lines.push(cols.map(c => esc((c.value || ((x) => x[c.key]))(r))).join(',')));
        const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url; a.download = `${csvName}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <div className="ur-toolbar">
                <input {...recentSearch.inputProps} className="ur-search" placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} />
                {recentSearch.menu}
                <button type="button" className="ur-chip" onClick={exportCsv} disabled={!shown.length}>
                    <FontAwesomeIcon icon={faDownload} /> CSV
                </button>
            </div>
            {shown.length === 0 ? <div className="ur-empty">{empty}</div> : (
                <div className="ur-table-wrap">
                    <table className="ur-table">
                        <thead>
                            <tr>
                                {cols.map(c => (
                                    <th
                                        key={c.key}
                                        className={c.num ? 'num' : ''}
                                        onClick={() => setSort(s => ({ key: c.key, dir: s.key === c.key ? -s.dir : -1 }))}
                                    >
                                        {c.label}{sort.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((r, i) => (
                                <tr key={r._id || i} onClick={onRowClick ? () => { recentSearch.save(); onRowClick(r); } : undefined} style={onRowClick ? { cursor: 'pointer' } : undefined}>
                                    {cols.map(c => (
                                        <td key={c.key} className={c.num ? 'num' : ''}>
                                            {c.render ? c.render(r) : r[c.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};
